// @flow
/*
	*  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
	*  This file is licensed to you under the Apache License, Version 2.0 (the "License");
	*  you may not use this file except in compliance with the License. You may obtain a copy
	*  of the License at http://www.apache.org/licenses/LICENSE-2.0
	*
	*  Unless required by applicable law or agreed to in writing, software distributed under
	*  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
	*  OF ANY KIND, either express or implied. See the License for the specific language
	*  governing permissions and limitations under the License.
	*
	*/

// add context guid and component name to each of the data
import getConfigApps from '../config';
import DeviceDetectionUtil from './DeviceDetectionUtil';
import AppsUtil from './AppsUtil';
import DownloadUtil from './DownloadUtil';
import CompatibilityUtil from './CompatibilityUtil';
import CCDUtil from './CCDUtil';
import {getCookie} from './cookie';
import Url from './Url';

export const thorDetectData = {
	workflow : 'CC Downloads',
	subCategory: 'detect',
	type : 'detect',
	subType : 'thor'
};

export const downloadingData = {
	workflow : 'CC Downloads',
	subCategory : 'Downloading',
	type : 'render',
	subType : 'downloading-app'
};


export const welcomeData = {
	workflow : 'CC Downloads',
	subCategory : 'Welcome',
	type : 'render',
	subType : 'susi',
	componentName : 'Welcome'
};

export const surveyData = {
	workflow : 'CC Downloads',
	subCategory : 'Survey',
	type : 'render',
	subType : 'page',
	componentName : 'Survey'
};

export const msgToastData = {
	workflow : 'CC Downloads',
	subCategory : 'Downloading Previous Version',
	type : 'render',
	subType : 'toast',
	componentName : 'Toast'
};

export const clickData = {
	workflow : 'CC Downloads',
	subCategory : 'Downloading',
	type : 'click'
	// add subtype
};

export const EVENT_TYPE = {
	RENDER: 'render',
	CLICK: 'click',
	DOWNLOAD: 'download',
	FEEDBACK: 'feedback'
};

export const prevVersionData = {
	workflow : 'CC Downloads',
	subCategory : 'Downloading Previous Version',
	type : 'render',
	subType : 'downloading-app'
};

export const blueBoxClickData = {
	workflow : 'CC Downloads',
	subCategory : 'Downloading Previous Version',
	type : 'click'
	// add subType
};

export const errorData = {
	workflow : 'CC Downloads',
	subCategory : 'Downloading Incompatible',
	type : 'render',
	subType : 'error'
};

export const errorClickData = {
	workflow : 'CC Downloads',
	subCategory : 'Downloading Incompatible',
	type : 'click'
	// add subtype
};

export const mobileData = {
	workflow : 'CC Downloads',
	subCategory : 'Downloading Mobile Incompatible',
	type : 'render',
	subType : 'error',
	errorType : 'Mobile'
};

export const mobileClickData = {
	workflow : 'CC Downloads',
	subCategory : 'Downloading Mobile Incompatible',
	type : 'click',
	errorType : 'Mobile'
	// add subtype
};

export const imslibfail = {
	workflow: 'CC Downloads',
	type: 'redirect-ccd',
	subType: 'ccd-bootstrap-failed',
	errorDesc: 'IMS Failure'
};

export function getContextGuid(notCompatibleOS: boolean, hasThor: ?boolean, outcome: string | boolean) {
	let guid = notCompatibleOS ? 'os-incompatible' : 'os-iscompatible';
	guid += `/entry=cch/thor=${(!!hasThor).toString()}/outcome=${outcome.toString()}`;
	return guid;
}

export function sendIngestLoggedOutPromotionExpEvent(productId: string, eventType?: string, eventParam?: string) {
	const environment = getConfigApps('env');
	const ingestUrl = `${getConfigApps('servers').Ingest}/ingest`;
	const userGUID = AppsUtil.getImsData().isSignedUser ? AppsUtil.getImsData().userId : undefined;
	const isPromotion = AppsUtil.isLoggedOutPromotionExp;
	const productDetails = CCDUtil.getProductsData(productId);
	const sapCode = productDetails.sapCode;
	let campaign = 'kaizen';
	switch (eventType) {
		case 'ccpd-dl-start-thor':
			campaign = 'subsequent-install';
			break;
		case 'email-me-link-button-clicked':
			campaign = 'mobile-device';
			break;
	}

	const data: Object = {
		eventCode: 'ACCC_SERVICE',
		eventType,
		userGUID,
		isThorPresent: AppsUtil.hasThor,
		params: {
			productId: productDetails.id,
			locale: AppsUtil.locale,
			osName: DeviceDetectionUtil.isWin() ? 'win' : 'mac',
			userAgent: navigator.userAgent,
			campaign,
			contextData: DownloadUtil.getWamContextData(sapCode),
			url: window.document.location.href,
			referer: getCookie('creative-cloud-referrer') || undefined,
			isPromotion,
			productSAPCode: sapCode,
			productVersion: (!AppsUtil.isMobileOrTab() && CompatibilityUtil.getSupportedAppVersion(sapCode)) || undefined
		}
	};
	if (isPromotion) {
		data.params.promotionWorkflow = Url.getParam('workflow');
		data.params.exp = {
			campaign_id: Url.getParam('campaignKey') // eslint-disable-line camelcase
		};
	}
	// If any custom params needs to be passed.
	if (eventType === 'ccpd-dl-start' && eventParam === 'ccpd-dl-through-try') {
		data.params['try-again'] = true;
	}
	const event = {
		project: 'kaizen-service',
		environment,
		ingesttype: 'dunamis',
		time: (new Date()).toISOString(),
		data
	};
	const cchome = getConfigApps('cchome');
	const headers = {
		'Content-Type': 'application/json',
		'x-api-key': cchome.clientId
	};

	CCDUtil.postData(ingestUrl, JSON.stringify({events: [event]}), headers, 3000);
}
